// ─────────────────────────────────────────────
//  MessagingLauncher — the app-wide mount point for the 💬 Messages entry
//  (D-01). The app uses per-page topbars (BoardPage, MessagesPage, …), so to
//  guarantee the entry is present on EVERY page with a SINGLE mount we anchor it
//  in the app shell as a fixed top-right launcher rather than editing each
//  page's header. It shares the one MessagingProvider subscription set.
//
//  • Renders nothing when there is no signed-in user or on the /login route
//    (messaging is for authenticated roster members only).
//  • Sits top-right, above the page topbars (z above the 56px sticky bar), so it
//    never overlaps the existing controls and is reachable on board / room /
//    messages / daily-list / setup alike.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import MessagingTopbarButton from "./MessagingTopbarButton";
import { isSandboxBoot } from "../sandbox/mode";
import { isNative } from "../native/platform";

// True while the board is in full-screen TV mode (BoardPage sets a body flag and
// fires "atc-tv-mode"). The wall display should not show the floating launcher.
function useTvMode() {
  const read = () =>
    typeof document !== "undefined" && document.body.getAttribute("data-tv-mode") === "1";
  const [tv, setTv] = useState(read);
  useEffect(() => {
    const update = () => setTv(read());
    update();
    window.addEventListener("atc-tv-mode", update);
    return () => window.removeEventListener("atc-tv-mode", update);
  }, []);
  return tv;
}

export default function MessagingLauncher() {
  const { user, isObserver, isBoardManager } = useAuth();
  const location = useLocation();
  const tvMode = useTvMode();

  // Messaging is inter-user communication, which the sandbox does not do.
  // An empty inbox reads as a broken feature; no inbox reads as a smaller app.
  if (isSandboxBoot()) return null;
  // Authenticated users only; never on the login screen.
  if (!user) return null;
  if (location.pathname === "/login") return null;
  // Observers are read-only monitors — no messaging. Board Managers are shared
  // wall devices with NO messaging access at all (also revoked in the rules).
  if (isObserver || isBoardManager) return null;
  // Hidden on the full-screen TV / wall display — it's a passive board there.
  if (tvMode) return null;
  // The provider "My Room" page already hosts its own inline Messages surface
  // (ProviderPage → Messages card), so suppress the floating launcher there to
  // avoid a duplicate entry.
  if (location.pathname === "/room") return null;
  // Same reason on /messages, which IS this panel as a full page — a floating
  // bubble that opens a drawer over the identical channel list is two doors to
  // one room. And on /home, where Messages is one of the four tiles: the bubble
  // sat in the corner of a screen whose whole job is to offer those four doors.
  if (location.pathname === "/messages" || location.pathname === "/home") return null;
  // Same rule on the native mobile shell, which has a permanent Messages TAB
  // in its bottom bar. The floating bubble was a second entry to the same
  // place — and, being web chrome, it rendered as a large light circle
  // hovering over the dark tab bar. Unread now shows as a blinking dot on the
  // Messages tab itself (MobileShell), which is the same signal in the place
  // the user already looks.
  if (isNative()) return null;

  return (
    <div style={styles.shell}>
      <MessagingTopbarButton />
    </div>
  );
}

const styles = {
  // Fixed BOTTOM-right floating launcher — clear of every page's top bar
  // controls (BoardPage's topbar packs Emergency/Urgency/profile/Sign out on the
  // top-right). A familiar chat-FAB position; opens the right-side MessagingPanel.
  shell: {
    position: "fixed",
    bottom: 16,
    right: 16,
    zIndex: 1000,
  },
};
